import { useState } from "react";
import { Link } from "react-router-dom";
import { EcartPrixDetail } from "@/composants/ecarts-prix";
import { Carte, EnTeteCarte } from "@/composants/ui/carte";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { messageDe, useChargement } from "@/lib/chargement";
import type { EcartPrix, FicheCommandeFournisseur, GesteEcartPrix, HausseFrais } from "@/lib/api";
import { api } from "@/lib/api";
import { euros, horodatage, natureFrais, pourcentage } from "@/lib/libelles";

/** Les trois gestes, chacun sa section (ticket #46). */
const SECTIONS: { geste: GesteEcartPrix; titre: string }[] = [
  { geste: "DECIDER", titre: "À valider" },
  { geste: "AUTORISER", titre: "À autoriser" },
  { geste: "CONFIRMER", titre: "À confirmer" },
];

/**
 * « Écarts de prix à valider » (LOT 2, tickets #39 et #46, décisions 33 et 39) :
 * ce que l'appelant peut faire, sur les commandes client où il agit. À valider :
 * il décide en une étape, dans la limite de son plafond. À autoriser : la hausse
 * dépasse ce que le chargé d'affaires concerné décide seul. À confirmer : un
 * écart autorisé sur une commande dont il est le chargé d'affaires, ou qu'une
 * habilitation lui ouvre. Le serveur trie ; l'écran ne fait que ranger.
 * Ticket #51 : en dessous, pour information, les hausses de frais acceptées
 * depuis trente jours, avec leur incidence sur ses commandes client.
 */
export function PageEcartsPrix() {
  const { donnees, erreur, enCours, recharger } = useChargement(async (signal) => {
    const [ecarts, hausses] = await Promise.all([
      api.ecartsPrixAValider(signal),
      api.haussesFrais(signal),
    ]);
    return { ecarts, hausses };
  }, []);
  const [refus, setRefus] = useState<string | null>(null);

  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (!donnees || enCours) return <Chargement message="Chargement des écarts de prix…" />;

  const agir = async (geste: () => Promise<FicheCommandeFournisseur>) => {
    setRefus(null);
    try {
      await geste();
      recharger();
      return true;
    } catch (cause) {
      setRefus(messageDe(cause));
      return false;
    }
  };
  const detail = (ecart: EcartPrix) => (
    <EcartPrixDetail
      ecart={ecart}
      avecCommande
      decider={(motif, nature_prix) =>
        agir(() =>
          api.deciderEcartPrix(ecart.commande_fournisseur_id, ecart.id, { motif, nature_prix }),
        )
      }
      contester={(motif) =>
        agir(() => api.contesterEcartPrix(ecart.commande_fournisseur_id, ecart.id, { motif }))
      }
      autoriser={() =>
        agir(() => api.autoriserEcartPrix(ecart.commande_fournisseur_id, ecart.id))
      }
    />
  );

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-encre">Écarts de prix à valider</h1>
        <p className="mt-1 text-sm text-encre-douce">
          Les prix d'AR qui diffèrent du prix retenu, sur les commandes où vous agissez. Au-delà
          de ce que le chargé d'affaires concerné décide seul, l'écart s'autorise, puis il le
          confirme. Accepté, le nouveau prix devient le prix retenu ; contesté, un problème
          fournisseur s'ouvre sur la ligne et la commande continue.
        </p>
      </div>
      {refus ? (
        <p role="alert" className="text-sm text-alerte">
          {refus}
        </p>
      ) : null}
      {donnees.ecarts.length === 0 ? (
        <Vide titre="Aucun écart à valider" detail="Rien n'attend votre décision." />
      ) : (
        SECTIONS.map(({ geste, titre }) => {
          const ecarts = donnees.ecarts.filter((ecart) => ecart.geste === geste);
          if (ecarts.length === 0) return null;
          return (
            <section key={geste} aria-label={titre}>
              <Carte>
                <EnTeteCarte>
                  <h2 className="text-sm font-semibold text-encre">
                    {titre} ({ecarts.length})
                  </h2>
                </EnTeteCarte>
                <ul className="divide-y divide-filet">
                  {ecarts.map((ecart) => (
                    <li key={ecart.id} className="px-4 py-3">
                      {detail(ecart)}
                    </li>
                  ))}
                </ul>
              </Carte>
            </section>
          );
        })
      )}
      <HaussesFrais hausses={donnees.hausses} />
    </div>
  );
}

const TITRE_HAUSSES = "Pour information : hausses de frais acceptées";

/**
 * Ticket #51, décision 49 : les hausses de frais acceptées ces trente derniers
 * jours, sur les commandes client que l'appelant voit — le serveur le filtre.
 * Rien à décider : la hausse, qui l'a acceptée, et pour chaque commande client
 * sa part et sa marge prévisionnelle sur achats sans la hausse et aujourd'hui.
 */
function HaussesFrais({ hausses }: { hausses: HausseFrais[] }) {
  return (
    <section aria-label={TITRE_HAUSSES}>
      <Carte>
        <EnTeteCarte>
          <h2 className="text-sm font-semibold text-encre">
            {TITRE_HAUSSES} ({hausses.length})
          </h2>
          <p className="mt-1 text-xs text-encre-douce">
            Les trente derniers jours, sur vos commandes client. Aucune confirmation n'est
            attendue de vous.
          </p>
        </EnTeteCarte>
        {hausses.length === 0 ? (
          <Vide titre="Aucune hausse de frais acceptée ces trente derniers jours" />
        ) : (
          <ul className="divide-y divide-filet">
            {hausses.map((hausse) => (
              <li key={hausse.ecart_id} className="px-4 py-3">
                <HausseFraisDetail hausse={hausse} />
              </li>
            ))}
          </ul>
        )}
      </Carte>
    </section>
  );
}

function HausseFraisDetail({ hausse }: { hausse: HausseFrais }) {
  const le = horodatage(hausse.acceptee_le);
  const tolerance = pourcentage(hausse.tolerance_appliquee ?? 0);
  const acceptation =
    hausse.decideur === null
      ? `acceptée d'office le ${le}, dans la tolérance de ${tolerance} %`
      : `acceptée le ${le} par ${hausse.decideur}`;
  return (
    <article
      aria-label={`Hausse de frais : ${hausse.objet}`}
      className="flex flex-col gap-2 text-sm"
    >
      <div className="min-w-0">
        <p className="font-medium text-encre">
          Frais « {hausse.objet} » : hausse de{" "}
          <span className="tabular-nums">{euros(hausse.hausse_ht)}</span>, de{" "}
          <span className="tabular-nums">{euros(hausse.montant_precedent_ht)}</span> à{" "}
          <span className="tabular-nums">{euros(hausse.montant_ar_ht)}</span>
        </p>
        <p className="text-encre-douce">
          {hausse.nature_frais ? `Nature : ${natureFrais(hausse.nature_frais)} · ` : null}
          <Link
            to={`/commandes-fournisseurs/${hausse.commande_fournisseur_id}`}
            className="text-encre underline underline-offset-4"
          >
            Commande chez {hausse.fournisseur}
          </Link>
          {" · "}
          {acceptation}
        </p>
      </div>
      <ul className="flex flex-col gap-1">
        {hausse.commandes_clients.map((commande) => (
          <li key={commande.commande_client_id} className="tabular-nums text-encre">
            <span className="font-mono">{commande.numero_commande_client}</span> : sa part de la
            hausse{" "}
            {commande.part_hausse_ht === null
              ? "inconnue (un prix manque)"
              : euros(commande.part_hausse_ht)}
            {" ; "}marge prévisionnelle sur achats{" "}
            {commande.marge_ht === null || commande.marge_sans_hausse_ht === null
              ? "inconnue (un prix manque)"
              : `${euros(commande.marge_sans_hausse_ht)} sans cette hausse, ` +
                `${euros(commande.marge_ht)} aujourd'hui`}
            {commande.marge_incomplete ? " (marge incomplète : le coût ne compte pas tout)" : null}
          </li>
        ))}
      </ul>
    </article>
  );
}
